import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  CategoryPreference,
  DigestResult,
  NotificationPreferences,
  PreferenceCategory,
} from '@up2d8/contracts';
import { Button, Card, LoadingState, Select, useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';

/**
 * Meldingen per soort (Stap 16), op het instellingenscherm van de app.
 *
 * Per categorie twee schakelaars: push en e-mail. Bovenaan, grijs en zonder schakelaar, wat altijd
 * doorkomt - een noodmelding en een bericht dat de schrijver verplicht maakte - met de uitleg
 * waarom. Wie dat niet ziet, denkt dat hij alles heeft uitgezet en mist de brandoefening.
 *
 * De schakelaars zijn 48 px hoog: dit scherm wordt met een duim bediend (vloermodus, ONT-0).
 */
export function NotificationPreferencesCard() {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();

  const prefs = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/me/notification-preferences', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as NotificationPreferences;
    },
  });

  const save = useMutation({
    mutationFn: async (body: {
      categories?: CategoryPreference[];
      digest?: NotificationPreferences['digest'];
    }) => {
      const { data, error } = await api.PUT('/api/v1/me/notification-preferences', {
        body: body as never,
      });
      if (error || !data) throw error ?? new Error('Mislukt');
      return data as NotificationPreferences;
    },
    onSuccess: (data) => queryClient.setQueryData(['notification-preferences'], data),
    onError: () => toast.show({ title: t('prefs.failed'), tone: 'danger' }),
  });

  const digestNow = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/api/v1/me/notification-preferences/digest-now', {});
      if (error || !data) throw error ?? new Error('Mislukt');
      return data as DigestResult;
    },
    onSuccess: (result) =>
      toast.show({
        title: result.sent
          ? t('prefs.digestSent', { count: result.items })
          : t('prefs.digestEmpty'),
        tone: result.sent ? 'success' : 'info',
      }),
    onError: () => toast.show({ title: t('prefs.failed'), tone: 'danger' }),
  });

  if (prefs.isPending) return <LoadingState rows={4} label={t('common.loading')} />;
  if (prefs.isError) return null;
  const data = prefs.data;

  const toggle = (category: PreferenceCategory, channel: 'push' | 'email') => {
    const current = data.categories.find((item) => item.category === category);
    if (!current) return;
    save.mutate({ categories: [{ ...current, [channel]: !current[channel] }] });
  };

  return (
    <div className="flex flex-col gap-element" data-testid="notification-preferences">
      <Card>
        <h2 className="font-heading text-title-sm font-semibold text-text-primary">
          {t('prefs.title')}
        </h2>
        <p className="mt-1 text-content text-text-secondary">{t('prefs.hint')}</p>

        {/* Wat altijd doorkomt: grijs, zonder schakelaar, met de reden. */}
        <div className="mt-element rounded-card bg-background p-4" data-testid="prefs-mandatory">
          <p className="font-semibold text-text-secondary">{t('prefs.mandatoryTitle')}</p>
          <p className="mt-1 text-content text-text-muted">{t('prefs.mandatoryHint')}</p>
        </div>

        <ul className="mt-element flex flex-col divide-y divide-border-light">
          <li className="flex items-center justify-end gap-6 pb-2 text-caption font-semibold uppercase tracking-wide text-text-muted">
            <span className="w-14 text-center">{t('prefs.push')}</span>
            <span className="w-14 text-center">{t('prefs.email')}</span>
          </li>
          {data.categories.map((item) => (
            <li key={item.category} className="flex items-center justify-between gap-element py-2">
              <div className="min-w-0">
                <p className="font-medium text-text-primary">{t(`prefs.cat_${item.category}`)}</p>
                <p className="text-caption text-text-muted">
                  {t(`prefs.catHint_${item.category}`)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-6">
                <FloorSwitch
                  checked={item.push}
                  label={`${t(`prefs.cat_${item.category}`)} · ${t('prefs.push')}`}
                  onChange={() => toggle(item.category, 'push')}
                  testId={`pref-${item.category}-push`}
                />
                <FloorSwitch
                  checked={item.email}
                  disabled={!data.hasEmail}
                  label={`${t(`prefs.cat_${item.category}`)} · ${t('prefs.email')}`}
                  onChange={() => toggle(item.category, 'email')}
                  testId={`pref-${item.category}-email`}
                />
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <div className="flex items-center justify-between gap-element">
          <div>
            <h2 className="font-heading text-title-sm font-semibold text-text-primary">
              {t('prefs.digestTitle')}
            </h2>
            <p className="mt-1 text-content text-text-secondary">{t('prefs.digestHint')}</p>
          </div>
          <FloorSwitch
            checked={data.digest.enabled}
            label={t('prefs.digestTitle')}
            onChange={() =>
              save.mutate({ digest: { ...data.digest, enabled: !data.digest.enabled } })
            }
            testId="pref-digest"
          />
        </div>
        {data.digest.enabled ? (
          <div className="mt-element flex flex-wrap items-end gap-element">
            <div className="w-40">
              <Select
                label={t('prefs.digestHour')}
                value={String(data.digest.hour)}
                onChange={(event) =>
                  save.mutate({ digest: { enabled: true, hour: Number(event.target.value) } })
                }
              >
                {Array.from({ length: 24 }, (_, hour) => (
                  <option key={hour} value={String(hour)}>
                    {`${String(hour).padStart(2, '0')}:00`}
                  </option>
                ))}
              </Select>
            </div>
            {data.digestNowAllowed ? (
              <Button
                variant="secondary"
                size="lg"
                loading={digestNow.isPending}
                onClick={() => digestNow.mutate()}
                data-testid="pref-digest-now"
              >
                {t('prefs.digestNow')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </Card>
    </div>
  );
}

/** Een schakelaar met een raakvlak van 48 × 48 px. */
function FloorSwitch({
  checked,
  disabled,
  label,
  onChange,
  testId,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      data-testid={testId}
      className="flex h-12 w-14 items-center justify-center disabled:opacity-40"
    >
      <span
        className={
          checked
            ? 'relative h-7 w-12 rounded-pill bg-brand-primary transition-colors'
            : 'relative h-7 w-12 rounded-pill bg-border transition-colors'
        }
      >
        <span
          className={
            checked
              ? 'absolute left-6 top-1 h-5 w-5 rounded-full bg-surface shadow transition-all'
              : 'absolute left-1 top-1 h-5 w-5 rounded-full bg-surface shadow transition-all'
          }
        />
      </span>
    </button>
  );
}
